import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Presentation, HelpCircle, Trash2, Clock, Star } from 'lucide-react';
import { AdminEducationModule, useDeleteModule } from '@/hooks/useEducationAdmin';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';

interface ModuleCardProps {
  module: AdminEducationModule;
  onEdit: (module: AdminEducationModule) => void;
  onManageSlides: (module: AdminEducationModule) => void;
  onManageQuiz: (module: AdminEducationModule) => void;
}

export const ModuleCard: React.FC<ModuleCardProps> = ({ 
  module, 
  onEdit, 
  onManageSlides, 
  onManageQuiz 
}) => {
  const deleteModule = useDeleteModule();

  const handleDelete = async () => {
    await deleteModule.mutateAsync(module.id);
  };

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg line-clamp-2">
            {module.title}
          </CardTitle>
          <Badge variant={module.is_required ? "default" : "secondary"}>
            {module.is_required ? "Required" : "Optional"}
          </Badge>
        </div>
        
        {module.category?.name && (
          <Badge variant="outline" className="w-fit">
            {module.category.name}
          </Badge>
        )}
        
        {module.description && (
          <CardDescription className="line-clamp-3">
            {module.description}
          </CardDescription>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Clock className="h-3 w-3" />
            <span>{module.duration_minutes}min</span>
          </div>
          <div className="flex items-center space-x-1">
            <Star className="h-3 w-3" />
            <span>{module.points} pts</span>
          </div>
        </div>

        <div className="text-sm">
          <div className="text-muted-foreground">Content Type:</div>
          <Badge variant="outline">{module.content_type}</Badge>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(module)}
          >
            <Edit className="mr-1 h-3 w-3" />
            Edit
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManageSlides(module)}
          >
            <Presentation className="mr-1 h-3 w-3" />
            Slides
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManageQuiz(module)}
          >
            <HelpCircle className="mr-1 h-3 w-3" />
            Quiz
          </Button>
          
          <ConfirmDialog
            title="Delete Module"
            description="Are you sure you want to delete this module? This action cannot be undone and will also delete all associated slides and quiz questions."
            confirmText="Delete"
            variant="destructive"
            onConfirm={handleDelete}
            isLoading={deleteModule.isPending}
            trigger={
              <Button variant="outline" size="sm">
                <Trash2 className="mr-1 h-3 w-3" />
                Delete
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};